import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { HoverHintDefinitionGrid } from "@/components/ui/HoverHintDefinitionGrid";
import { buildPurchaseOrderPeekRows } from "@/lib/purchaseOrderPeekRows";
import {
  fetchPurchaseOrderDetail,
  purchaseOrderDetailQueryKey,
  PURCHASE_ORDER_DETAIL_STALE_MS,
} from "@/pages/mrp/purchaseOrderApi";

import { PurchaseOrderPeekHoverHintBody } from "./entityPeekBodies";
import type { PurchaseOrderPeekContext } from "./entityPeekTypes";

export function PurchaseOrderDetailHoverHint({
  purchaseOrderId,
  context,
  emptyMessage,
}: {
  purchaseOrderId: number;
  context?: PurchaseOrderPeekContext;
  emptyMessage?: string;
}): JSX.Element {
  const detailQuery = useQuery({
    queryKey: purchaseOrderDetailQueryKey(purchaseOrderId),
    queryFn: () => fetchPurchaseOrderDetail(purchaseOrderId),
    staleTime: PURCHASE_ORDER_DETAIL_STALE_MS,
  });

  if (detailQuery.isLoading) {
    return (
      <p className="flex items-center gap-1.5 text-xs text-slate-600">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        Loading purchase order???
      </p>
    );
  }

  if (detailQuery.isError || detailQuery.data == null) {
    return (
      <PurchaseOrderPeekHoverHintBody
        context={context}
        emptyMessage={emptyMessage ?? "Could not load purchase order preview."}
      />
    );
  }

  return (
    <HoverHintDefinitionGrid
      rows={buildPurchaseOrderPeekRows(
        detailQuery.data.purchase_order,
        detailQuery.data.lines,
        context,
      )}
    />
  );
}
